import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Shield, Zap, Calendar, Car, Clock, MapPin, Star, ChevronRight } from 'lucide-react';
import Logo from '../components/Logo';
import { Button } from '../components/ui/button';

const HomePage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const toggleLanguage = () => {
    const newLang = i18n.language === 'es' ? 'en' : 'es';
    i18n.changeLanguage(newLang);
    localStorage.setItem('language', newLang);
  };

  const features = [
    { icon: Shield, title: t('hero.features.secure'), desc: 'Vigilancia continua' },
    { icon: Zap, title: t('hero.features.fast'), desc: 'QR instantáneo' },
    { icon: Calendar, title: t('hero.features.easy'), desc: 'En 2 minutos' },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-heavy">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <Logo className="h-14" />
          </Link>
          
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/reservar" className="text-zinc-300 hover:text-primary transition-colors" data-testid="nav-reserve">
              {t('nav.reserve')}
            </Link>
            <Link to="/login" className="text-zinc-300 hover:text-primary transition-colors" data-testid="nav-login">
              {t('nav.login')}
            </Link>
            <button 
              onClick={toggleLanguage}
              className="text-sm text-zinc-400 hover:text-white transition-colors"
              data-testid="language-toggle"
            >
              {i18n.language === 'es' ? 'EN' : 'ES'}
            </button>
          </nav>

          <div className="flex md:hidden items-center gap-4">
            <button 
              onClick={toggleLanguage}
              className="text-sm text-zinc-400"
              data-testid="language-toggle-mobile"
            >
              {i18n.language === 'es' ? 'EN' : 'ES'}
            </button>
            <Button 
              onClick={() => navigate('/reservar')}
              className="btn-primary text-sm px-4 py-2"
              data-testid="mobile-reserve-btn"
            >
              {t('nav.reserve')}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/c0cb83c7-9d4b-4785-b4e7-ab2b7a0a926c/images/f83a86162c30f89a1eacd2bac28d25c66d0ce33e0c90474c35f9e176972fe9c4.png"
            alt="Valet Parking Terminal 4 Madrid Barajas - Servicio Premium"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-background" />
        </div>

        <div className="relative z-10 container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-extrabold text-white mb-6">
              {t('hero.title')}
              <span className="block text-primary">PRIVILEGE</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-zinc-300 max-w-2xl mx-auto mb-8">
              {t('hero.subtitle')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                onClick={() => navigate('/reservar')}
                className="btn-primary text-lg px-8 py-4 gold-glow"
                data-testid="hero-cta-btn"
              >
                {t('hero.cta')}
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
              
              <Link 
                to="/login"
                className="btn-secondary px-8 py-4 text-white"
                data-testid="hero-login-btn"
              >
                {t('nav.login')}
              </Link>
            </div>
          </motion.div>

          {/* Features */}
          <motion.div 
            className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-16 max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            {features.map((feature, index) => (
              <div 
                key={`feature-${feature.title}`}
                className="glass-light p-6 rounded-sm text-center"
                data-testid={`feature-${index}`}
              >
                <feature.icon className="w-10 h-10 text-primary mx-auto mb-4" />
                <h3 className="font-heading font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-zinc-400">{feature.desc}</p>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-primary rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* How it Works */}
      <section className="py-24 bg-gradient-dark">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
              {i18n.language === 'es' ? '¿Cómo Funciona?' : 'How It Works'}
            </h2>
            <p className="text-zinc-400 max-w-xl mx-auto">
              {i18n.language === 'es' 
                ? 'Reserva tu plaza en 3 sencillos pasos' 
                : 'Book your spot in 3 easy steps'}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { 
                step: '01', 
                icon: Calendar, 
                title: i18n.language === 'es' ? 'Elige Fechas' : 'Choose Dates',
                desc: i18n.language === 'es' ? 'Selecciona día de entrada y salida.' : 'Select entry and exit dates.'
              },
              { 
                step: '02', 
                icon: Car, 
                title: i18n.language === 'es' ? 'Datos del Vehículo' : 'Vehicle Details',
                desc: i18n.language === 'es' ? 'Introduce la matrícula y tus datos de contacto.' : 'Enter license plate and contact details.'
              },
              { 
                step: '03', 
                icon: Clock, 
                title: i18n.language === 'es' ? 'Confirma y Listo' : 'Confirm & Done',
                desc: i18n.language === 'es' ? 'Paga online o en efectivo. Recibe tu QR.' : 'Pay online or cash. Get your QR code.'
              },
            ].map((item, index) => (
              <motion.div
                key={`step-${item.step}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="card-dark p-8 relative overflow-hidden group"
                data-testid={`step-${index}`}
              >
                <span className="absolute top-4 right-4 font-heading text-6xl font-extrabold text-white/5 group-hover:text-primary/10 transition-colors">
                  {item.step}
                </span>
                <item.icon className="w-12 h-12 text-primary mb-6" />
                <h3 className="font-heading font-bold text-xl text-white mb-3">{item.title}</h3>
                <p className="text-zinc-400">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="card-dark p-8 md:p-12 relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
              
              <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
                <div>
                  <h2 className="font-heading text-3xl font-bold text-white mb-4">
                    {i18n.language === 'es' ? 'Tarifas Simples' : 'Simple Pricing'}
                  </h2>
                  <div className="flex items-baseline gap-2 mb-4">
                    <span className="font-heading text-5xl font-extrabold text-primary">12€</span>
                    <span className="text-zinc-400">/ {i18n.language === 'es' ? 'día' : 'day'}</span>
                  </div>
                  <ul className="space-y-2 text-zinc-300">
                    <li className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-primary" />
                      {i18n.language === 'es' ? 'Precio único por día' : 'Single daily rate'}
                    </li>
                    <li className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-primary" />
                      {i18n.language === 'es' ? 'Reserva con antelación' : 'Book in advance'}
                    </li>
                    <li className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-primary" />
                      {i18n.language === 'es' ? 'Pago online o efectivo' : 'Online or cash payment'}
                    </li>
                  </ul>
                </div>
                
                <Button 
                  onClick={() => navigate('/reservar')}
                  className="btn-primary px-8 py-4 text-lg whitespace-nowrap"
                  data-testid="pricing-cta-btn"
                >
                  {t('hero.cta')}
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="py-24 bg-gradient-dark">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl font-bold text-white mb-6">
                <MapPin className="inline w-8 h-8 text-primary mr-2" />
                {i18n.language === 'es' ? 'Ubicación' : 'Location'}
              </h2>
              <p className="text-zinc-300 mb-6">
                {i18n.language === 'es' 
                  ? 'Privilege Parking está ubicado estratégicamente para tu comodidad. Acceso fácil y seguro las 24 horas.'
                  : 'Privilege Parking is strategically located for your convenience. Easy and secure access 24/7.'}
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-zinc-300">
                  <Shield className="w-5 h-5 text-primary" />
                  <span>{i18n.language === 'es' ? 'Vigilancia 24/7' : '24/7 Surveillance'}</span>
                </div>
                <div className="flex items-center gap-3 text-zinc-300">
                  <Car className="w-5 h-5 text-primary" />
                  <span>{i18n.language === 'es' ? 'Plazas amplias' : 'Spacious spots'}</span>
                </div>
                <div className="flex items-center gap-3 text-zinc-300">
                  <Zap className="w-5 h-5 text-primary" />
                  <span>{i18n.language === 'es' ? 'Check-in/out rápido' : 'Fast check-in/out'}</span>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="rounded-sm overflow-hidden"
            >
              <img 
                src="https://images.unsplash.com/photo-1769641241035-73d5a8fe7dbf?w=800&q=80"
                alt="Parking"
                className="w-full h-80 object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-card border-t border-white/5">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <Logo className="h-14" />
            </div>
            
            <nav className="flex items-center gap-6 text-sm text-zinc-400">
              <Link to="/reservar" className="hover:text-primary transition-colors">
                {t('nav.reserve')}
              </Link>
              <Link to="/login" className="hover:text-primary transition-colors">
                {t('nav.login')}
              </Link>
              <Link to="/worker/login" className="hover:text-primary transition-colors">
                {t('nav.worker')}
              </Link>
              <Link to="/admin/login" className="hover:text-primary transition-colors">
                {t('nav.admin')}
              </Link>
            </nav>
            
            <p className="text-sm text-zinc-500">
              © 2026 Privilege Parking. privilegeparking.es
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
